// Per-tab session store for unsent drafts. A draft is cleared only when the
// server accepted the message (a rejected or too-long message keeps its text).
//
// A draft is the user's own unsent text (possibly privileged work product), and
// sessionStorage outlives a sign-out and a reload in the same tab. So the key
// names the signed-in user: another user signing in on the tab never reads it,
// even when a composer unmounting after sign-out writes it back. AuthContext
// also sweeps other users' drafts away (clearDrafts) on sign-out and sign-in.
const PREFIX = 'gchat:draft:';

const ownerPart = (owner: string): string => encodeURIComponent(owner.trim().toLowerCase());

export const draftKey = (owner: string, caseId: string, chatId: string | null): string =>
  `${PREFIX}${ownerPart(owner)}:${caseId}:${chatId ?? 'new'}`;

function store(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function loadDraft(key: string): string {
  try {
    return store()?.getItem(key) ?? '';
  } catch {
    return '';
  }
}

export function saveDraft(key: string, text: string): void {
  try {
    const s = store();
    if (!s) return;
    if (text === '') s.removeItem(key);
    else s.setItem(key, text);
  } catch {
    // Storage full or blocked: a draft is a convenience, never an error.
  }
}

export function clearDraft(key: string): void {
  try {
    store()?.removeItem(key);
  } catch {
    // ignore
  }
}

/** Remove every stored draft, except those of `keepOwner` when one is given. */
export function clearDrafts(keepOwner?: string | null): void {
  try {
    const s = store();
    if (!s) return;
    const keep = keepOwner ? `${PREFIX}${ownerPart(keepOwner)}:` : null;
    const doomed: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k && k.startsWith(PREFIX) && !(keep && k.startsWith(keep))) doomed.push(k);
    }
    for (const k of doomed) s.removeItem(k);
  } catch {
    // ignore
  }
}
